import * as assert from 'assert';
import { computed, observable, when } from 'mobx';
import { Artifact } from '../_interfaces/Artifact';
import { Collection, INewCollection, ServerCollection } from '../_interfaces/Collection';
import { CollectionService } from '../_services/CollectionService';
import { AStoreWithMappedData } from './AStoreWithMappedData';
import { ArtifactStore } from './ArtifactStore';
import { FamilyStore } from './FamilyStore';
import { ViewHistoryStore } from './ViewHistoryStore';

export class CollectionStore extends AStoreWithMappedData<Collection> {
    private _collectionService: CollectionService;
    private _artifactStore: ArtifactStore;
    private _viewHistoryStore: ViewHistoryStore;
    private _familyStore: FamilyStore;
    @observable
    private _currentlyEditingCollection?: Collection;
    @observable
    private _lastClickedCollection: Collection | undefined;
    private current_user_id: number;

    public constructor(authToken: string, current_user_id: number, artifactStore: ArtifactStore, viewHistoryStore: ViewHistoryStore, familyStore: FamilyStore) {
        super();
        this.current_user_id = current_user_id;
        this._collectionService = new CollectionService(authToken);
        this._artifactStore = artifactStore;
        this._viewHistoryStore = viewHistoryStore;
        this._familyStore = familyStore;
        this.loadData();
    }

    public get collections(): Array<Collection> {
        return this.allRecords();
    }

    public getSingleCollectionById(id: number): Collection {
        return this.getSingleRecord(id);
    }

    public createNewCollection(title: string): Promise<number> {
        const newCollection: INewCollection = { title };
        return this._collectionService.create(newCollection);
    }

    public reloadData(): Promise<void> {
        return this.loadData();
    }

    @computed
    public get collectionToEdit() {
        return this._currentlyEditingCollection;
    }

    @computed
    public get lastClickedCollection(): Collection | undefined {
        return this._lastClickedCollection;
    }

    public setCollectionToEdit(collection: Collection): void {
        this._currentlyEditingCollection = collection;
    }

    public setSetLastClickedCollection(collection: Collection | undefined): void {
        this._lastClickedCollection = collection;
    }

    protected loadData(): Promise<void> {
        return this.loadCollections()
            .then(() => {
                return this.loadViewHistory();
            })
            .then(() => {
                return this.loadArtifacts();
            })
            .then(() => {
                return this.loadFamilies();
            });
    }

    protected loadCollections(): Promise<void> {
        return this._collectionService.getAll().then((serverCollections: Array<ServerCollection>) => {
            const collections: Array<Collection> = serverCollections.map(serverCollection => {
                return new Collection(serverCollection, this.current_user_id);
            });
            this.addRecordsToMap(collections);
        });
    }

    protected loadArtifacts(): Promise<void> {
        return new Promise((resolve, reject) => {
            when(
                () => {
                    return this._artifactStore.storeIsReady;
                },
                () => {
                    Array.from(this.recordsMap.keys()).forEach(collectionId => {
                        const collection = this.recordsMap.get(collectionId);
                        if (collection) {
                            const artifacts = this._artifactStore.getArtifacts(collection.artifact_ids) || [];
                            collection.artifacts = artifacts;
                            artifacts.forEach(artifact => artifact.addParentCollection(collection));
                            this.recordsMap.set(collectionId, collection);
                        }
                    });
                    return resolve();
                }
            );
        });
    }

    public getCollections(ids: Array<number>): Array<Collection> {
        return this.getRecords(ids);
    }

    private loadViewHistory(): Promise<void> {
        return new Promise((resolve, reject) => {
            when(
                () => {
                    return this._viewHistoryStore.storeIsReady;
                },
                () => {
                    Array.from(this._viewHistoryStore.collectionViewHistoryMap.keys()).forEach(key => {
                        const record = this.recordsMap.get(key);
                        if (record) {
                            const history = this._viewHistoryStore.collectionViewHistoryMap.get(key);
                            if (history) {
                                record.last_viewed = history.last_viewed;
                                this.recordsMap.set(key, record);
                            }
                        }
                    });
                    return resolve();
                }
            );
        });
    }

    private loadFamilies(): Promise<void> {
        return new Promise((resolve, reject) => {
            when(
                () => {
                    return this._familyStore.storeIsReady;
                },
                () => {
                    this._familyStore.allRecords().forEach(family => {
                        family.collection_ids.forEach(collection_id => {
                            const collection: Collection | undefined = this.recordsMap.get(collection_id);
                            if (collection) {
                                collection.families.push(family);
                            } else {
                                assert(false, 'CollectionStore#loadFamilies - encountered family w/ unknown collection ID: ' + collection_id);
                            }
                        });
                    });
                    return resolve();
                }
            );
        });
    }

    public updateCollectionViewHistory(recordId: number): void {
        const record = this.recordsMap.get(recordId);
        if (record) {
            record.last_viewed = new Date().getTime();
            this.recordsMap.set(recordId, record);
        }
    }

    public create(args: INewCollection): Promise<number> {
        return this._collectionService.create(args).then((new_id: number) => {
            return this.updateStore().then(() => {
                return new_id;
            });
        });
    }

    public addArtifactToCollection(collectionId: number, artifact: Artifact): Promise<any> {
        // used for adding an existing artifact to an existing collection when they both exist client-side
        return this._collectionService.addArtifactToCollection(collectionId, artifact.id).then(() => {
            this.loadData();
        });
    }

    public addNewlyCreatedArtifactToCollection(collectionId: number, artifactId: number): Promise<any> {
        // used for adding a newly created artifact to a collection.  Does not reload the client side data model,
        // the code calling this method is responsible for knowing when it's time to reload.
        return this._collectionService.addArtifactToCollection(collectionId, artifactId);
    }

    public removeArtifactFromCollection(collection_id: number, artifact_id: number): void {
        this._collectionService.removeArtifactFromCollection(collection_id, artifact_id).then(() => {
            this.loadData();
        });
    }
}



// WEBPACK FOOTER //
// ./src/_stores/CollectionStore.ts